import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  Box,
  Stack,
  Text,
  Button,
  IconButton,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Badge,
} from "@chakra-ui/react";
import { useEffect, useState, useCallback } from "react";
import axios from "../axiosConfig";
import { ChatState } from "../Context/ChatProvider";
import ChatLoading from "./ChatLoading";
import GroupChatModal from "./miscellaneous/GroupChatModal";
import socket from "../socket";
import { isMobile } from "react-device-detect";

const getRecipient = (loggedUser, users) => {
  if (!users || !loggedUser) return "Unknown";
  const recipient = users.find((user) => user._id !== loggedUser._id);
  return recipient ? recipient.name : "Unknown";
};

const MyChats = ({ fetchAgain }) => {
  const [loggedUser, setLoggedUser] = useState(null);
  const [rightClickedChat, setRightClickedChat] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);

  const {
    selectedChat,
    setSelectedChat,
    user,
    chats,
    setChats,
    unreadCounts,
    setUnreadCounts,
  } = ChatState();

  const toast = useToast();

  const fetchChats = useCallback(async () => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${user.token}` },
      };
      const { data } = await axios.get("/api/chat", config);
      setChats(data);
    } catch (error) {
      toast({
        title: "Error Occurred!",
        description: "Failed to load chats.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-left",
      });
    }
  }, [user.token, setChats, toast]);

  const handleLongPressStart = (chat) => {
    if (!isMobile) return;
    const timer = setTimeout(() => setRightClickedChat(chat), 500);
    setLongPressTimer(timer);
  };

  const handleLongPressEnd = () => {
    if (!isMobile) return;
    clearTimeout(longPressTimer);
  };

  const handleRightClick = (e, chat) => {
    e.preventDefault();
    setRightClickedChat(chat);
  };

  const openDeleteConfirmation = (chat) => {
    setChatToDelete(chat);
    setIsModalOpen(true);
  };

  const deleteChat = async (chat) => {
    try {
      await axios.delete(`/api/chat/${chat._id}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      toast({
        title: "Chat Deleted",
        description: "This chat has been successfully deleted.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "bottom-left",
      });
      fetchChats();
      setIsModalOpen(false);
    } catch (error) {
      toast({
        title: "Error Occurred!",
        description: "Unable to delete the chat.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-left",
      });
    }
  };

  useEffect(() => {
    const userInfo = JSON.parse(localStorage.getItem("userInfo"));
    setLoggedUser(userInfo);
    fetchChats();

    if (!socket.connected) {
      socket.connect();
      socket.emit("setup", user);
    }

    socket.emit("get online users");

    socket.on("online users", (userIds) => {
      setOnlineUsers(userIds);
    });

    const handleMessageReceived = (newMessageReceived) => {
      console.log("[SOCKET] Message Received:", newMessageReceived);

      const chatId = newMessageReceived?.chat?._id;
      if (!chatId) {
        console.warn("[SOCKET] Message received without valid chat ID");
        return;
      }

      console.log("[SOCKET] Chat ID to update:", chatId);

      fetchChats(); // Refresh chat list to get latest server state
      console.log("[SOCKET] fetchChats() triggered for real-time update");

      if (!selectedChat || selectedChat._id !== chatId) {
        console.log("[SOCKET] Chat not open, updating chat preview list");

        setChats((prevChats) =>
          prevChats.map((chat) =>
            chat._id === chatId
              ? { ...chat, latestMessage: newMessageReceived }
              : chat
          )
        );

        const unread = JSON.parse(localStorage.getItem("unreadMessages")) || {};
        unread[chatId] = (unread[chatId] || 0) + 1;
        localStorage.setItem("unreadMessages", JSON.stringify(unread));

        console.log(
          `[SOCKET] Unread count for chat ${chatId}:`,
          unread[chatId]
        );
      } else {
        console.log(
          "[SOCKET] Chat is currently open, updating latest message inside chat window"
        );

        setSelectedChat((prev) => ({
          ...prev,
          latestMessage: newMessageReceived,
        }));
      }
    };

    socket
      .off("message received")
      .on("message received", handleMessageReceived);

    return () => {
      socket.off("message received", handleMessageReceived);
      socket.off("online users");
    };
  }, [user, selectedChat, setChats, setSelectedChat, fetchChats]);

  return (
    <Box
      display={{ base: selectedChat ? "none" : "flex", md: "flex" }}
      flexDir="column"
      alignItems="center"
      p={3}
      bg="white"
      w={{ base: "100%", md: "30%" }}
      borderRadius="lg"
      borderWidth="1px"
      borderColor="#e2e8f0"
    >
      <Box
        pb={3}
        px={3}
        fontSize={{ base: "24px", md: "26px" }}
        fontWeight="bold"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        w="100%"
      >
        Message Center
        <GroupChatModal>
          <Button
            fontSize="14px"
            bg="#4e64ee"
            color="white"
            _hover={{ bg: "#3b4fc9" }}
            rightIcon={<AddIcon />}
          >
            New Group
          </Button>
        </GroupChatModal>
      </Box>

      <Box
        display="flex"
        flexDir="column"
        p={3}
        w="100%"
        h="100%"
        borderRadius="lg"
        overflowY="scroll"
      >
        {chats ? (
          <Stack spacing={2}>
            {(() => {
              const selfChat = chats.find(
                (chat) =>
                  chat &&
                  !chat.isGroupChat &&
                  chat.users &&
                  chat.users.length === 1 &&
                  chat.users[0]._id === loggedUser?._id
              );

              const otherChats = chats.filter(
                (chat) => !(selfChat && chat._id === selfChat._id)
              );

              const renderChatBox = (chat, label = null) => {
                if (!chat || typeof chat !== "object") return null;

                const isGroupAdmin =
                  chat.isGroupChat && chat.groupAdmin?._id === loggedUser?._id;

                const allowDelete = !chat.isGroupChat || isGroupAdmin;

                const isRecipientOnline =
                  !chat.isGroupChat &&
                  chat.users?.length === 2 &&
                  onlineUsers.includes(
                    chat.users.find((u) => u._id !== loggedUser?._id)?._id
                  );

                return (
                  <Box
                    key={chat._id}
                    onClick={() => {
                      setSelectedChat(chat);
                      setUnreadCounts((prev) => ({ ...prev, [chat._id]: 0 }));
                    }}
                    onContextMenu={(e) => handleRightClick(e, chat)}
                    onTouchStart={() => handleLongPressStart(chat)}
                    onTouchEnd={handleLongPressEnd}
                    cursor="pointer"
                    bg={
                      rightClickedChat === chat
                        ? "red.400"
                        : selectedChat === chat
                        ? "#4e64ee"
                        : "#f7f7f7"
                    }
                    color={
                      rightClickedChat === chat || selectedChat === chat
                        ? "white"
                        : "black"
                    }
                    px={3}
                    py={2}
                    borderRadius="lg"
                    position="relative"
                    _hover={{ bg: selectedChat !== chat && "#edf2f7" }}
                  >
                    <Text
                      fontWeight="semibold"
                      noOfLines={1}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box
                        flex="1"
                        pr={2}
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {label
                          ? label
                          : !chat.isGroupChat
                          ? getRecipient(loggedUser, chat.users)
                          : chat.chatName}
                        {isRecipientOnline && (
                          <Badge
                            ml={2}
                            colorScheme="green"
                            variant="solid"
                            borderRadius="full"
                          >
                            Online
                          </Badge>
                        )}
                      </Box>
                      {unreadCounts[chat._id] > 0 && (
                        <Badge
                          ml={2}
                          colorScheme="red"
                          borderRadius="full"
                          px={2}
                          flexShrink={0}
                        >
                          {unreadCounts[chat._id]}
                        </Badge>
                      )}
                    </Text>
                    {chat.latestMessage?.sender && (
                      <Text fontSize="xs" noOfLines={1}>
                        <b>
                          {chat.latestMessage.sender._id === loggedUser._id
                            ? "Me"
                            : chat.latestMessage.sender.name}
                          :
                        </b>{" "}
                        {chat.latestMessage.content.length > 50
                          ? `${chat.latestMessage.content.slice(0, 50)}...`
                          : chat.latestMessage.content}
                      </Text>
                    )}
                    {rightClickedChat === chat && allowDelete && (
                      <IconButton
                        icon={<DeleteIcon />}
                        colorScheme="red"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteConfirmation(chat);
                        }}
                        position="absolute"
                        top={2}
                        right={2}
                      />
                    )}
                  </Box>
                );
              };

              return (
                <>
                  {selfChat && renderChatBox(selfChat, "Notes")}
                  {otherChats.map((chat) => renderChatBox(chat))}
                </>
              );
            })()}
          </Stack>
        ) : (
          <ChatLoading />
        )}
      </Box>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Chat</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete{" "}
            <b>
              {chatToDelete?.isGroupChat
                ? `${chatToDelete.chatName} (Group)`
                : getRecipient(loggedUser, chatToDelete?.users || [])}
            </b>
            ?
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" onClick={() => deleteChat(chatToDelete)}>
              Yes
            </Button>
            <Button ml={3} onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default MyChats;
